import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  Tooltip as MuiTooltip,
} from '@mui/material';
import {
  Refresh,
  TrendingUp,
  PhotoCamera,
  Group,
  Videocam,
  Assessment,
  Timeline,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { formatLocalDate, formatLocalDateTime, convertUtcToLocalHour } from '../../utils/dateUtils';
import {
  LineChart,
  Line,
  BarChart as RechartsBarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { PieLabelRenderProps } from 'recharts';

interface PaymentBreakdown {
  credits: number;
  ads: number;
}

interface DailyAnalytics {
  date: string;
  photo_swap: number;
  group_photo_swap: number;
  video_swap: number;
  enhance_image: number;
  restore_image: number;
  total: number;
  totalCreditsUsed: number;
  paidWithCredits: number;
  paidWithAds: number;
  paymentBreakdown: {
    photo_swap: PaymentBreakdown;
    group_photo_swap: PaymentBreakdown;
    video_swap: PaymentBreakdown;
    enhance_image: PaymentBreakdown;
    restore_image: PaymentBreakdown;
  };
  uniqueUsersCount: number;
  uniqueDevicesCount: number;
}

interface DeviceAnalytics {
  deviceId: string;
  totalSwaps: number;
  photoSwaps: number;
  groupSwaps: number;
  videoSwaps: number;
  enhanceSwaps: number;
  restoreSwaps: number;
  platform: string;
  lastSwap: string;
  firstSwap: string;
}

interface UserAnalytics {
  userId: string;
  firebaseUserId: string;
  email?: string;
  displayName?: string;
  totalSwaps: number;
  photoSwaps: number;
  groupSwaps: number;
  videoSwaps: number;
  enhanceSwaps: number;
  restoreSwaps: number;
  uniqueDevicesCount: number;
  lastSwap: string;
  firstSwap: string;
}

const SwapAnalytics: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Date filters
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [swapTypeFilter, setSwapTypeFilter] = useState<string>('');
  const [timezone, setTimezone] = useState<string>('UTC');

  // Daily analytics
  const [dailyAnalytics, setDailyAnalytics] = useState<DailyAnalytics[]>([]);
  const [dailySummary, setDailySummary] = useState<any>(null);

  // Device analytics
  const [deviceAnalytics, setDeviceAnalytics] = useState<DeviceAnalytics[]>([]);

  // User analytics
  const [userAnalytics, setUserAnalytics] = useState<UserAnalytics[]>([]);

  // Overview analytics
  const [overview, setOverview] = useState<any>(null);
  const [hourlyAnalytics, setHourlyAnalytics] = useState<any[]>([]);
  const [templateAnalytics, setTemplateAnalytics] = useState<any[]>([]);
  const [successRateAnalytics, setSuccessRateAnalytics] = useState<any[]>([]);

  useEffect(() => {
    loadAnalytics();
  }, [tabValue, startDate, endDate, swapTypeFilter, timezone]);

      const loadAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      if (tabValue === 0) {
        // Overview
        const response = await apiService.getSwapAnalyticsOverview(
          startDate,
          endDate,
          timezone
        );
        if (response.success && response.data) {
          setOverview(response.data.overview || null);
        } else {
          setError('Failed to load overview');
        }
      } else if (tabValue === 1) {
        // Daily analytics
        const response = await apiService.getDailySwapAnalytics(
          startDate,
          endDate,
          swapTypeFilter || undefined,
          timezone
        );
        if (response.success && response.data) {
          setDailyAnalytics(response.data.analytics || []);
          setDailySummary(response.data.summary || null);
        } else {
          setError('Failed to load daily analytics');
        }
      } else if (tabValue === 2) {
        // Hourly analytics
        const response = await apiService.getHourlySwapAnalytics(
          startDate,
          endDate,
          swapTypeFilter || undefined,
          timezone
        );
        if (response.success && response.data) {
          setHourlyAnalytics(response.data.analytics || []);
        } else {
          setError('Failed to load hourly analytics');
        }
      } else if (tabValue === 3) {
        // Device analytics
        const response = await apiService.getDeviceSwapAnalytics(
          startDate,
          endDate,
          swapTypeFilter || undefined,
          undefined,
          timezone
        );
        if (response.success && response.data) {
          setDeviceAnalytics(response.data.devices || []);
        } else {
          setError('Failed to load device analytics');
        }
      } else if (tabValue === 4) {
        // User analytics
        const response = await apiService.getUserSwapAnalytics(
          startDate,
          endDate,
          swapTypeFilter || undefined,
          undefined,
          timezone
        );
        if (response.success && response.data) {
          setUserAnalytics(response.data.users || []);
        } else {
          setError('Failed to load user analytics');
        }
      } else if (tabValue === 5) {
        // Template analytics
        const response = await apiService.getTemplateSwapAnalytics(
          startDate,
          endDate,
          undefined,
          timezone
        );
        if (response.success && response.data) {
          setTemplateAnalytics(response.data.templates || []);
        } else {
          setError('Failed to load template analytics');
        }
      } else if (tabValue === 6) {
        // Success rate analytics
        const response = await apiService.getSuccessRateAnalytics(
          startDate,
          endDate,
          swapTypeFilter || undefined,
          timezone
        );
        if (response.success && response.data) {
          setSuccessRateAnalytics(response.data.analytics || []);
        } else {
          setError('Failed to load success rate analytics');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const getSwapTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      photo_swap: 'Photo Swap',
      group_photo_swap: 'Group Photo Swap',
      video_swap: 'Video Swap',
      enhance_image: 'Enhance Image',
      restore_image: 'Restore Image',
    };
    return labels[type] || type;
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4">Swap Analytics</Typography>
        <Button
          startIcon={<Refresh />}
          onClick={loadAnalytics}
          variant="outlined"
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="End Date"
                type="date"
                fullWidth
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Swap Type</InputLabel>
                <Select
                  value={swapTypeFilter}
                  label="Swap Type"
                  onChange={(e) => setSwapTypeFilter(e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="photo_swap">Photo Swap</MenuItem>
                  <MenuItem value="group_photo_swap">Group Photo Swap</MenuItem>
                  <MenuItem value="video_swap">Video Swap</MenuItem>
                  <MenuItem value="enhance_image">Enhance Image</MenuItem>
                  <MenuItem value="restore_image">Restore Image</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={timezone}
                  label="Timezone"
                  onChange={(e) => setTimezone(e.target.value)}
                >
                  <MenuItem value="UTC">UTC</MenuItem>
                  <MenuItem value="Asia/Kolkata">Asia/Kolkata (IST)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} variant="scrollable" scrollButtons="auto">
          <Tab label="Overview" icon={<Assessment />} iconPosition="start" />
          <Tab label="Daily Analytics" icon={<BarChartIcon />} iconPosition="start" />
          <Tab label="Hourly Analytics" icon={<Timeline />} iconPosition="start" />
          <Tab label="Device Analytics" />
          <Tab label="User Analytics" />
          <Tab label="Template Usage" />
          <Tab label="Success Rate" />
        </Tabs>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {tabValue === 0 && (
              <Box p={3}>
                {overview ? (
                  <>
                    {/* Key Metrics */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary">
                              Total Swaps
                            </Typography>
                            <Typography variant="h4">{overview.totalSwaps || 0}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary">
                              Success Rate
                            </Typography>
                            <Typography variant="h4" color="success.main">
                              {overview.successRate?.toFixed(1) || 0}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {overview.successCount || 0} successful / {overview.failureCount || 0} failed
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary">
                              Unique Users
                            </Typography>
                            <Typography variant="h4">{overview.uniqueStats?.uniqueUsersCount || 0}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary">
                              Unique Devices
                            </Typography>
                            <Typography variant="h4">{overview.uniqueStats?.uniqueDevicesCount || 0}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    {/* Swap Type Distribution */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} md={6}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>Swap Type Distribution</Typography>
                            <ResponsiveContainer width="100%" height={300}>
                              <PieChart>
                                <Pie
                                  data={[
                                    { name: 'Photo Swap', value: overview.typeStats?.photo_swap || 0 },
                                    { name: 'Group Swap', value: overview.typeStats?.group_photo_swap || 0 },
                                    { name: 'Video Swap', value: overview.typeStats?.video_swap || 0 },
                                  ]}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={({ name, percent }: PieLabelRenderProps) => {
                                    const percentValue = typeof percent === 'number' ? percent : 0;
                                    return `${name} ${(percentValue * 100).toFixed(0)}%`;
                                  }}
                                  outerRadius={80}
                                  fill="#8884d8"
                                  dataKey="value"
                                >
                                  <Cell fill="#8884d8" />
                                  <Cell fill="#82ca9d" />
                                  <Cell fill="#ffc658" />
                                </Pie>
                                <RechartsTooltip />
                              </PieChart>
                            </ResponsiveContainer>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>Platform Distribution</Typography>
                            <ResponsiveContainer width="100%" height={300}>
                              <RechartsBarChart data={[
                                { name: 'Android', value: overview.platformStats?.android || 0 },
                                { name: 'iOS', value: overview.platformStats?.ios || 0 },
                              ]}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <RechartsTooltip />
                                <Bar dataKey="value" fill="#8884d8" />
                              </RechartsBarChart>
                            </ResponsiveContainer>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    {/* Template Usage */}
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Template Usage</Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="subtitle2" color="text.secondary">Template Swaps</Typography>
                            <Typography variant="h4">{overview.templateUsage?.templateSwaps || 0}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {overview.templateUsage?.templateUsageRate || 0}% of total
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="subtitle2" color="text.secondary">Non-Template Swaps</Typography>
                            <Typography variant="h4">{overview.templateUsage?.nonTemplateSwaps || 0}</Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                    No overview data available
                  </Typography>
                )}
              </Box>
            )}

            {tabValue === 1 && (
              <Box p={3}>
                {dailySummary && (
                  <>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} sm={6} md={2}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary">
                              Total Swaps
                            </Typography>
                            <Typography variant="h4">{dailySummary.totalSwaps || 0}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary">
                              Photo Swaps
                            </Typography>
                            <Typography variant="h5" color="primary">
                              {dailySummary.totalPhotoSwaps || 0}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary">
                              Group Swaps
                            </Typography>
                            <Typography variant="h5" color="secondary">
                              {dailySummary.totalGroupSwaps || 0}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary">
                              Video Swaps
                            </Typography>
                            <Typography variant="h5" color="success.main">
                              {dailySummary.totalVideoSwaps || 0}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary">
                              Enhance Image
                            </Typography>
                            <Typography variant="h5" color="info.main">
                              {dailySummary.totalEnhanceSwaps || 0}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary">
                              Restore Image
                            </Typography>
                            <Typography variant="h5" color="info.dark">
                              {dailySummary.totalRestoreSwaps || 0}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                  </>
                )}

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Photo</TableCell>
                        <TableCell align="right">Group</TableCell>
                        <TableCell align="right">Video</TableCell>
                        <TableCell align="right">Enhance</TableCell>
                        <TableCell align="right">Restore</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">💳 Credits</TableCell>
                        <TableCell align="right">📺 Ads</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dailyAnalytics.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              No analytics data found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        dailyAnalytics.map((item) => (
                          <TableRow key={item.date} hover>
                            <TableCell>
                              {formatLocalDate(item.date, 'MMM dd, yyyy')}
                            </TableCell>
                            <TableCell align="right">
                              <MuiTooltip title={`💳 Credits: ${item.paymentBreakdown?.photo_swap?.credits || 0} | 📺 Ads: ${item.paymentBreakdown?.photo_swap?.ads || 0}`} arrow>
                                <span style={{ cursor: 'help' }}>{item.photo_swap}</span>
                              </MuiTooltip>
                            </TableCell>
                            <TableCell align="right">
                              <MuiTooltip title={`💳 Credits: ${item.paymentBreakdown?.group_photo_swap?.credits || 0} | 📺 Ads: ${item.paymentBreakdown?.group_photo_swap?.ads || 0}`} arrow>
                                <span style={{ cursor: 'help' }}>{item.group_photo_swap}</span>
                              </MuiTooltip>
                            </TableCell>
                            <TableCell align="right">
                              <MuiTooltip title={`💳 Credits: ${item.paymentBreakdown?.video_swap?.credits || 0} | 📺 Ads: ${item.paymentBreakdown?.video_swap?.ads || 0}`} arrow>
                                <span style={{ cursor: 'help' }}>{item.video_swap}</span>
                              </MuiTooltip>
                            </TableCell>
                            <TableCell align="right">
                              <MuiTooltip title={`💳 Credits: ${item.paymentBreakdown?.enhance_image?.credits || 0} | 📺 Ads: ${item.paymentBreakdown?.enhance_image?.ads || 0}`} arrow>
                                <span style={{ cursor: 'help' }}>{item.enhance_image || 0}</span>
                              </MuiTooltip>
                            </TableCell>
                            <TableCell align="right">
                              <MuiTooltip title={`💳 Credits: ${item.paymentBreakdown?.restore_image?.credits || 0} | 📺 Ads: ${item.paymentBreakdown?.restore_image?.ads || 0}`} arrow>
                                <span style={{ cursor: 'help' }}>{item.restore_image || 0}</span>
                              </MuiTooltip>
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="bold">{item.total}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={item.paidWithCredits || 0}
                                color="primary"
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={item.paidWithAds || 0}
                                color="secondary"
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {tabValue === 2 && (
              <Box p={3}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Hourly Swap Distribution</Typography>
                {hourlyAnalytics.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={(() => {
                      // Group by date and hour, aggregate swap types
                      // Convert UTC hours to local timezone
                      const grouped: { [key: string]: any } = {};
                      hourlyAnalytics.forEach(item => {
                        // Convert UTC date and hour to local timezone
                        const { localDate, localHour } = convertUtcToLocalHour(item.date, item.hour);
                        const key = `${localDate}_${localHour}`;
                        
                        if (!grouped[key]) {
                          // Format date in local timezone for display
                          const formattedDate = formatLocalDate(localDate, 'MMM dd');
                          grouped[key] = {
                            time: `${formattedDate} ${String(localHour).padStart(2, '0')}:00`,
                            date: localDate,
                            hour: localHour,
                            photo_swap: 0,
                            group_photo_swap: 0,
                            video_swap: 0
                          };
                        }
                        grouped[key][item.swapType] = item.count;
                      });
                      return Object.values(grouped).sort((a: any, b: any) => {
                        if (a.date !== b.date) return a.date.localeCompare(b.date);
                        return a.hour - b.hour;
                      });
                    })()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="photo_swap" stroke="#8884d8" name="Photo Swap" />
                      <Line type="monotone" dataKey="group_photo_swap" stroke="#82ca9d" name="Group Swap" />
                      <Line type="monotone" dataKey="video_swap" stroke="#ffc658" name="Video Swap" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                    No hourly analytics data found
                  </Typography>
                )}
              </Box>
            )}

            {tabValue === 3 && (
              <Box p={3}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Device ID</TableCell>
                        <TableCell>Platform</TableCell>
                        <TableCell align="right">Photo</TableCell>
                        <TableCell align="right">Group</TableCell>
                        <TableCell align="right">Video</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell>First Swap</TableCell>
                        <TableCell>Last Swap</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deviceAnalytics.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              No device analytics found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        deviceAnalytics.map((device) => (
                          <TableRow key={device.deviceId} hover>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {device.deviceId.substring(0, 16)}...
                            </TableCell>
                            <TableCell>
                              <Chip label={device.platform} size="small" />
                            </TableCell>
                            <TableCell align="right">{device.photoSwaps}</TableCell>
                            <TableCell align="right">{device.groupSwaps}</TableCell>
                            <TableCell align="right">{device.videoSwaps}</TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="bold">{device.totalSwaps}</Typography>
                            </TableCell>
                            <TableCell>
                              {formatLocalDateTime(device.firstSwap, 'MMM dd, yyyy HH:mm')}
                            </TableCell>
                            <TableCell>
                              {formatLocalDateTime(device.lastSwap, 'MMM dd, yyyy HH:mm')}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {tabValue === 4 && (
              <Box p={3}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell align="right">Photo</TableCell>
                        <TableCell align="right">Group</TableCell>
                        <TableCell align="right">Video</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Devices</TableCell>
                        <TableCell>First Swap</TableCell>
                        <TableCell>Last Swap</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {userAnalytics.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              No user analytics found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        userAnalytics.map((user) => (
                          <TableRow key={user.userId} hover>
                            <TableCell>
                              {user.displayName || 'Unknown'}
                            </TableCell>
                            <TableCell>{user.email || 'N/A'}</TableCell>
                            <TableCell align="right">{user.photoSwaps}</TableCell>
                            <TableCell align="right">{user.groupSwaps}</TableCell>
                            <TableCell align="right">{user.videoSwaps}</TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="bold">{user.totalSwaps}</Typography>
                            </TableCell>
                            <TableCell align="right">{user.uniqueDevicesCount}</TableCell>
                            <TableCell>
                              {formatLocalDateTime(user.firstSwap, 'MMM dd, yyyy HH:mm')}
                            </TableCell>
                            <TableCell>
                              {formatLocalDateTime(user.lastSwap, 'MMM dd, yyyy HH:mm')}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {tabValue === 5 && (
              <Box p={3}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Template Usage Analytics</Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Template ID</TableCell>
                        <TableCell align="right">Usage Count</TableCell>
                        <TableCell>Swap Types</TableCell>
                        <TableCell align="right">Avg Processing Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {templateAnalytics.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              No template analytics found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        templateAnalytics.map((template) => (
                          <TableRow key={template.templateId} hover>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                              {template.templateId}
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="bold">{template.count}</Typography>
                            </TableCell>
                            <TableCell>
                              {template.swapTypes?.map((type: string) => (
                                <Chip key={type} label={getSwapTypeLabel(type)} size="small" sx={{ mr: 0.5 }} />
                              ))}
                            </TableCell>
                            <TableCell align="right">
                              {template.avgProcessingTime > 0 
                                ? `${(template.avgProcessingTime / 1000).toFixed(1)}s`
                                : 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {tabValue === 6 && (
              <Box p={3}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Success Rate Analytics</Typography>
                {successRateAnalytics.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={400} style={{ marginBottom: 24 }}>
                      <RechartsBarChart data={successRateAnalytics}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" angle={-45} textAnchor="end" height={100} />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Bar dataKey="successful" stackId="a" fill="#82ca9d" name="Successful" />
                        <Bar dataKey="failed" stackId="a" fill="#ff6b6b" name="Failed" />
                      </RechartsBarChart>
                    </ResponsiveContainer>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Swap Type</TableCell>
                            <TableCell align="right">Total</TableCell>
                            <TableCell align="right">Successful</TableCell>
                            <TableCell align="right">Failed</TableCell>
                            <TableCell align="right">Success Rate</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {successRateAnalytics.map((item: any, index: number) => (
                            <TableRow key={index} hover>
                              <TableCell>{formatLocalDate(item.date, 'MMM dd, yyyy')}</TableCell>
                              <TableCell>{getSwapTypeLabel(item.swapType)}</TableCell>
                              <TableCell align="right">{item.total}</TableCell>
                              <TableCell align="right" sx={{ color: 'success.main' }}>
                                {item.successful}
                              </TableCell>
                              <TableCell align="right" sx={{ color: 'error.main' }}>
                                {item.failed}
                              </TableCell>
                              <TableCell align="right">
                                <Typography fontWeight="bold">
                                  {item.successRate?.toFixed(1) || 0}%
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                ) : (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                    No success rate analytics found
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default SwapAnalytics;

